import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { ApiService } from './api.service';
import { LoggerService } from './logger.service';
import { AttendanceService } from './attendance.service';

/** Minutes of no browser activity before a checked-in staff member shows Offline. */
export const ATTENDANCE_IDLE_MS = 5 * 60 * 1000;
/** Keep last_seen fresh while the staff browser is active. */
export const ATTENDANCE_HEARTBEAT_MS = 60 * 1000;

type PresenceState = 'online' | 'offline';

/**
 * While the logged-in staff user is checked in, track browser activity and report
 * online/offline presence so the admin employee list can show In vs Offline.
 */
@Injectable({ providedIn: 'root' })
export class AttendancePresenceService implements OnDestroy {
  private readonly api = inject(ApiService);
  private readonly logger = inject(LoggerService);
  private readonly attendance = inject(AttendanceService);
  private readonly ngZone = inject(NgZone);

  private checkedInSub: Subscription | null = null;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private tracking = false;
  private presence: PresenceState = 'online';
  private readonly activityHandler = () => this.onActivity();
  private readonly visibilityHandler = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      this.onActivity();
    }
  };

  /** Start listening to check-in state (safe to call once from app shell). */
  init(): void {
    if (this.checkedInSub) {
      return;
    }
    // Track presence for both checked_in and on_break states
    this.checkedInSub = this.attendance.currentAttendance$.subscribe((record) => {
      const isActivelyWorking = record?.work_status === 'checked_in' || record?.work_status === 'on_break';
      if (isActivelyWorking) {
        this.startTracking();
      } else {
        this.stopTracking();
      }
    });
  }

  ngOnDestroy(): void {
    this.checkedInSub?.unsubscribe();
    this.checkedInSub = null;
    this.stopTracking();
  }

  private startTracking(): void {
    if (this.tracking || typeof window === 'undefined') {
      return;
    }
    this.tracking = true;
    this.presence = 'online';
    this.bindActivityListeners();
    this.scheduleIdleTimer();
    this.startHeartbeat();
    this.reportPresence('online');
  }

  private stopTracking(): void {
    if (!this.tracking && !this.idleTimer && !this.heartbeatTimer) {
      this.unbindActivityListeners();
      return;
    }
    this.tracking = false;
    this.clearIdleTimer();
    this.clearHeartbeat();
    this.unbindActivityListeners();
  }

  private bindActivityListeners(): void {
    window.addEventListener('mousemove', this.activityHandler, { passive: true });
    window.addEventListener('mousedown', this.activityHandler, { passive: true });
    window.addEventListener('keydown', this.activityHandler, { passive: true });
    window.addEventListener('scroll', this.activityHandler, { passive: true, capture: true });
    window.addEventListener('touchstart', this.activityHandler, { passive: true });
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  private unbindActivityListeners(): void {
    if (typeof window === 'undefined') {
      return;
    }
    window.removeEventListener('mousemove', this.activityHandler);
    window.removeEventListener('mousedown', this.activityHandler);
    window.removeEventListener('keydown', this.activityHandler);
    window.removeEventListener('scroll', this.activityHandler, true);
    window.removeEventListener('touchstart', this.activityHandler);
    document.removeEventListener('visibilitychange', this.visibilityHandler);
  }

  private onActivity(): void {
    if (!this.tracking) {
      return;
    }
    this.scheduleIdleTimer();
    if (this.presence === 'offline') {
      this.presence = 'online';
      this.reportPresence('online');
      this.startHeartbeat();
    }
  }

  private scheduleIdleTimer(): void {
    this.clearIdleTimer();
    // Run idle timer outside Angular to avoid change-detection churn from activity events.
    this.ngZone.runOutsideAngular(() => {
      this.idleTimer = setTimeout(() => {
        this.ngZone.run(() => this.onIdle());
      }, ATTENDANCE_IDLE_MS);
    });
  }

  private onIdle(): void {
    if (!this.tracking || this.presence === 'offline') {
      return;
    }
    this.presence = 'offline';
    this.clearHeartbeat();
    this.reportPresence('offline');
  }

  private startHeartbeat(): void {
    this.clearHeartbeat();
    this.ngZone.runOutsideAngular(() => {
      this.heartbeatTimer = setInterval(() => {
        if (this.tracking && this.presence === 'online') {
          // Heartbeat only refreshes last_seen — transitions publish with notify=true.
          this.reportPresence('online', false);
        }
      }, ATTENDANCE_HEARTBEAT_MS);
    });
  }

  private clearIdleTimer(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  private clearHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private reportPresence(presence: PresenceState, notify = true): void {
    this.api.post('attendant/attendance/presence', { presence, notify }).subscribe({
      error: (err) => {
        this.logger.warn('Failed to report attendance presence', err, 'AttendancePresenceService');
      },
    });
  }
}
